import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { cn } from "../lib/utils";
import {
  sanitizeTemplate,
  type EodFormAudience,
  type EodFormQuestion,
  type EodFormTemplate,
} from "../lib/eod-form-builder";

function groupBySection(questions: EodFormQuestion[]) {
  const groups: Array<{ section: string; questions: EodFormQuestion[] }> = [];
  const lookup = new Map<string, { section: string; questions: EodFormQuestion[] }>();
  for (const q of questions) {
    const section = q.section.trim() || "General";
    const existing = lookup.get(section);
    if (existing) { existing.questions.push(q); continue; }
    const group = { section, questions: [q] };
    lookup.set(section, group);
    groups.push(group);
  }
  return groups;
}

interface QuestionFieldProps {
  question: EodFormQuestion;
  value: unknown;
  onChange: (value: unknown) => void;
  error: string | undefined;
}

function QuestionField({ question, value, onChange, error }: QuestionFieldProps) {
  const strValue = value !== undefined && value !== null ? String(value) : "";

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium leading-none">
        {question.label}
        {question.required && <span className="ml-0.5 text-[var(--brand-primary)]"> *</span>}
      </Label>

      {question.description && (
        <p className="text-xs leading-5 text-muted-foreground">{question.description}</p>
      )}

      {question.type === "short_text" && (
        <Input
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className={cn(error && "border-red-500 focus-visible:ring-red-500")}
        />
      )}

      {question.type === "long_text" && (
        <Textarea
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className={cn("min-h-24", error && "border-red-500 focus-visible:ring-red-500")}
        />
      )}

      {question.type === "number" && (
        <Input
          type="number"
          value={strValue}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          placeholder={question.placeholder || "0"}
          className={cn(error && "border-red-500 focus-visible:ring-red-500")}
        />
      )}

      {question.type === "currency" && (
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
          <Input
            type="number"
            value={strValue}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            placeholder={question.placeholder || "0"}
            className={cn("pl-7", error && "border-red-500 focus-visible:ring-red-500")}
          />
        </div>
      )}

      {question.type === "date" && (
        <Input
          type="date"
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          className={cn(error && "border-red-500 focus-visible:ring-red-500")}
        />
      )}

      {question.type === "yes_no" && (
        <div className="flex gap-2">
          {(["Yes", "No"] as const).map((opt) => {
            const optValue = opt.toLowerCase();
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(optValue)}
                className={cn(
                  "flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all",
                  value === optValue
                    ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
                    : "border-border bg-background text-foreground hover:border-[var(--brand-primary)]/40 hover:bg-muted/40",
                  error && value === undefined && "border-red-500",
                )}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}

      {question.type === "rating" && (
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: question.scaleMax }, (_, i) => {
            const ratingValue = i + 1;
            const isSelected = value === ratingValue;
            return (
              <button
                key={ratingValue}
                type="button"
                onClick={() => onChange(ratingValue)}
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold transition-all",
                  isSelected
                    ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white"
                    : "border-border bg-background text-foreground hover:border-[var(--brand-primary)]/40",
                )}
              >
                {ratingValue}
              </button>
            );
          })}
        </div>
      )}

      {question.type === "select" && (
        <div className="flex flex-wrap gap-2">
          {question.options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={cn(
                "rounded-lg border px-4 py-2 text-sm font-medium transition-all",
                value === opt
                  ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
                  : "border-border bg-background text-foreground hover:border-[var(--brand-primary)]/40 hover:bg-muted/40",
              )}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

interface EODFormProps {
  audience: EodFormAudience;
}

function getTodayString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export function EODForm({ audience }: EODFormProps) {
  const [template, setTemplate] = useState<EodFormTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadTemplate() {
      try {
        const response = await fetch(`/api/eod-form-public?audience=${audience}`);
        const result = await response.json();
        if (!result.success) throw new Error(result.error || "Failed to load form");
        if (!cancelled) {
          const tmpl = sanitizeTemplate(audience, result.data);
          setTemplate(tmpl);

          // Pre-fill date fields with today's date so the user can just submit
          const today = getTodayString();
          const defaults: Record<string, unknown> = {};
          for (const q of tmpl.questions) {
            if (q.type === "date") {
              defaults[q.key] = today;
            }
          }
          if (Object.keys(defaults).length > 0) {
            setAnswers((prev) => ({ ...defaults, ...prev }));
          }
        }
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Failed to load form");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    loadTemplate();
    return () => { cancelled = true; };
  }, [audience]);

  const setAnswer = (key: string, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    if (!template) return false;
    const newErrors: Record<string, string> = {};
    for (const q of template.questions) {
      if (!q.required) continue;
      const val = answers[q.key];
      if (val === undefined || val === null || val === "") {
        newErrors[q.key] = "This field is required";
      }
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      // Scroll to first error
      const firstKey = Object.keys(newErrors)[0];
      document.getElementById(`field_${firstKey}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || !template) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = { audience, answers, formTitle: template.title };
      console.log("[EOD Form] Submitting:", JSON.stringify({ audience, answerKeys: Object.keys(answers), answerCount: Object.keys(answers).length }));
      const response = await fetch("/api/eod-submissions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!result.success) {
        const detail = result.missingFields
          ? `Missing required fields: ${(result.missingFields as string[]).join(", ")}. Please fill them out and try again.`
          : result.error || "Submission failed";
        throw new Error(detail);
      }
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }

  // ── Load error ──────────────────────────────────────────────────────────────

  if (loadError || !template) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background p-6 text-center">
        <BrandLogo variant="primary" className="mb-2 h-8 w-auto opacity-60" />
        <p className="text-base font-semibold text-foreground">Form not available</p>
        <p className="max-w-sm text-sm text-muted-foreground">{loadError || "This form could not be loaded."}</p>
      </div>
    );
  }

  // ── Success ─────────────────────────────────────────────────────────────────

  if (isSubmitted) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <header className="border-b border-border/60 px-6 py-4">
          <BrandLogo variant="primary" className="h-8 w-auto" />
        </header>
        <div className="flex flex-1 flex-col items-center justify-center gap-6 p-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-foreground">Report submitted!</h1>
            <p className="max-w-sm text-sm leading-6 text-muted-foreground">
              Your EOD has been received. Good work today — see you tomorrow.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ────────────────────────────────────────────────────────────────────

  const groups = groupBySection(template.questions);

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 border-b border-border/60 bg-background/95 px-6 py-4 backdrop-blur">
        <BrandLogo variant="primary" className="h-8 w-auto" />
      </header>

      {/* Form content */}
      <main className="mx-auto max-w-2xl px-4 pb-32 pt-8 sm:px-6">
        {/* Heading */}
        <div className="mb-8 space-y-2">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">{template.title}</h1>
          {template.description && (
            <p className="text-sm leading-6 text-muted-foreground">{template.description}</p>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-10">
          {groups.map((group) => (
            <div key={group.section}>
              {/* Section header */}
              <div className="mb-5 flex items-center gap-3">
                <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--brand-primary)]">
                  {group.section}
                </span>
                <div className="h-px flex-1 bg-border/50" />
              </div>

              {/* Questions */}
              <div className="space-y-6">
                {group.questions.map((question) => (
                  <div key={question.id} id={`field_${question.key}`}>
                    <QuestionField
                      question={question}
                      value={answers[question.key]}
                      onChange={(val) => setAnswer(question.key, val)}
                      error={errors[question.key]}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Submit error */}
        {submitError && (
          <div className="mt-8 rounded-xl border border-red-500/25 bg-red-500/8 px-4 py-3 text-sm text-red-600 dark:text-red-400">
            {submitError}
          </div>
        )}
      </main>

      {/* Sticky submit bar */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-border/60 bg-background/95 px-4 py-4 backdrop-blur">
        <div className="mx-auto max-w-2xl">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full"
            size="lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit EOD Report"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
